import { ArrowLeft, FileText, Handshake } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";
const AgencyPartnershipAgreement = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const lastUpdated = "January 31, 2026";
  return <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-black border-b border-white/10">
        
      </header>

      <main className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Title Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Handshake className="w-10 h-10 text-purple-500" />
            <h1 className="text-4xl md:text-5xl font-bold">Agency Partnership Agreement</h1>
          </div>
          <div className="flex flex-wrap gap-4 text-sm text-gray-400">
            <span>Last Updated: {lastUpdated}</span>
          </div>
        </div>

        {/* Important Notice */}
        <div className="mb-10 p-6 bg-purple-500/10 border border-purple-500/20 rounded-xl">
          <p className="text-purple-300">
            <strong>IMPORTANT:</strong> This Agreement governs the partnership between Metro City Groove and 
            agency partners accessing Creator Management or Venue Oversight dashboards.
          </p>
        </div>

        {/* Section 1 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">1. Introduction and Definitions</h2>
          <div className="space-y-4 text-gray-300">
            <p>
              This Agency Partnership Agreement ("Agreement") is entered into between Metro City Groove (Pty) Ltd 
              ("MCG," "we," "us," or "our") and the agency or business entity ("Partner," "Agency," "you," or "your") 
              registering for access to the MCG platform dashboards.
            </p>
            <p>
              <strong className="text-white">"Platform"</strong> refers to the Metro City Groove digital ecosystem, including but not limited to 
              the Creator Management Dashboard, Venue Oversight Dashboard, and Platform Control Dashboard.
            </p>
            <p>
              <strong className="text-white">"Services"</strong> refers to the tools, features, and functionalities provided through the Platform 
              for managing creators, venues, events, and related entertainment industry operations.
            </p>
          </div>
        </section>

        {/* Section 2 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">2. Partnership Eligibility</h2>
          <div className="space-y-4 text-gray-300">
            <p>To become an MCG Partner, you must:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>Be a legally registered business entity in good standing</li>
              <li>Have valid business registration with CIPC or equivalent authority</li>
              <li>Maintain appropriate business licenses for your industry</li>
              <li>Have a minimum of one (1) year operating history in the entertainment, hospitality, or related industry</li>
              <li>Provide accurate and complete information during the registration process</li>
              <li>Pass MCG's verification and due diligence process</li>
            </ul>
          </div>
        </section>

        {/* Section 3 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">3. Partner Obligations</h2>
          <div className="space-y-4 text-gray-300">
            <p>As an MCG Partner, you agree to:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>Maintain accurate and up-to-date business information on the Platform</li>
              <li>Comply with all applicable laws, regulations, and industry standards</li>
              <li>Ensure all creators and venues managed through the Platform meet MCG's quality standards</li>
              <li>Respond to booking requests and inquiries within 48 hours</li>
              <li>Maintain professional conduct in all Platform interactions</li>
              <li>Promptly report any issues, disputes, or concerns to MCG</li>
              <li>Protect confidential information and user data in accordance with POPIA</li>
              <li>Pay all applicable fees and commissions as outlined in your subscription plan</li>
            </ul>
          </div>
        </section>

        {/* Section 4 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">4. MCG Obligations</h2>
          <div className="space-y-4 text-gray-300">
            <p>MCG agrees to provide:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-400">
              <li>Access to the Platform and its features as specified in your subscription plan</li>
              <li>Technical support during business hours (Monday-Friday, 8:00-17:00 SAST)</li>
              <li>Regular Platform updates and maintenance</li>
              <li>Secure handling of business and user data</li>
              <li>Fair and transparent billing practices</li>
              <li>Mediation assistance for disputes between Partners and users</li>
            </ul>
          </div>
        </section>

        {/* Section 5 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">5. Fees and Payment Terms</h2>
          <div className="space-y-6 text-gray-300">
            <div>
              <h3 className="text-lg font-medium text-white mb-2">5.1 Subscription Fees</h3>
              <p className="text-gray-400">
                Partners agree to pay the monthly subscription fee associated with their selected dashboard access tier. Current pricing is:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-400 mt-2">
                <li>Creator Management Dashboard: R3,499/month</li>
                <li>Venue Oversight Dashboard: R5,299/month</li>
                <li>Platform Control (Both Dashboards): Custom pricing</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-medium text-white mb-2">5.2 Payment Terms</h3>
              <p className="text-gray-400">
                Subscription fees are due on the first of each month. Late payments may result in service suspension after a 7-day grace period.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-white mb-2">5.3 Commission Structure</h3>
              <p className="text-gray-400">
                MCG may charge transaction fees or commissions on bookings processed through the Platform. These will be disclosed in your dashboard.
              </p>
            </div>
          </div>
        </section>

        {/* Section 6 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">6. Intellectual Property</h2>
          <div className="space-y-6 text-gray-300">
            <div>
              <h3 className="text-lg font-medium text-white mb-2">6.1 MCG Property</h3>
              <p className="text-gray-400">
                The Platform, including all software, designs, trademarks, and content, remains the exclusive property of MCG.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-white mb-2">6.2 Partner Content</h3>
              <p className="text-gray-400">
                Partners retain ownership of their original content uploaded to the Platform but grant MCG a non-exclusive license to display and promote such content.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-white mb-2">6.3 Restrictions</h3>
              <p className="text-gray-400">
                Partners may not copy, modify, reverse engineer, or create derivative works from any MCG intellectual property without written consent.
              </p>
            </div>
          </div>
        </section>

        {/* Section 7 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">7. Confidentiality</h2>
          <div className="space-y-4 text-gray-300">
            <p>
              Both parties agree to maintain the confidentiality of proprietary information, business strategies, 
              user data, and any other information marked as confidential. This obligation survives termination 
              of this Agreement for a period of two (2) years.
            </p>
          </div>
        </section>

        {/* Section 8 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">8. Limitation of Liability</h2>
          <div className="space-y-4 text-gray-300">
            <p>
              <strong className="text-white">8.1</strong> MCG shall not be liable for indirect, incidental, special, consequential, or punitive 
              damages arising from or related to this Agreement or Platform use.
            </p>
            <p>
              <strong className="text-white">8.2</strong> MCG's total liability shall not exceed the fees paid by Partner in the twelve (12) 
              months preceding the claim.
            </p>
            <p>
              <strong className="text-white">8.3</strong> MCG does not guarantee the performance, reliability, or availability of third-party 
              services integrated with the Platform.
            </p>
          </div>
        </section>

        {/* Section 9 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">9. Termination</h2>
          <div className="space-y-6 text-gray-300">
            <div>
              <h3 className="text-lg font-medium text-white mb-2">9.1 By Partner</h3>
              <p className="text-gray-400">
                You may terminate this Agreement with 30 days written notice. No refunds will be issued for partial months.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-medium text-white mb-2">9.2 By MCG</h3>
              <p className="text-gray-400">MCG may terminate this Agreement immediately for:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-400 mt-2">
                <li>Breach of this Agreement or Terms of Service</li>
                <li>Fraudulent or illegal activity</li>
                <li>Non-payment of fees after the grace period</li>
                <li>Provision of false information during registration</li>
                <li>Conduct that damages MCG's reputation or Platform integrity</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-medium text-white mb-2">9.3 Effect of Termination</h3>
              <p className="text-gray-400">
                Upon termination, Partner's access to the Platform will be revoked, and any outstanding fees become immediately due.
              </p>
            </div>
          </div>
        </section>

        {/* Section 10 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">10. Dispute Resolution</h2>
          <div className="space-y-4 text-gray-300">
            <p>
              <strong className="text-white">10.1</strong> Parties agree to first attempt to resolve disputes through good-faith negotiation.
            </p>
            <p>
              <strong className="text-white">10.2</strong> If negotiation fails, disputes shall be submitted to mediation in Johannesburg, 
              South Africa, under the rules of the Arbitration Foundation of Southern Africa (AFSA).
            </p>
            <p>
              <strong className="text-white">10.3</strong> This Agreement is governed by the laws of the Republic of South Africa.
            </p>
          </div>
        </section>

        {/* Section 11 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">11. Amendments</h2>
          <div className="space-y-4 text-gray-300">
            <p>
              MCG reserves the right to modify this Agreement with 30 days notice. Continued use of the Platform 
              after such notice constitutes acceptance of the modified terms.
            </p>
          </div>
        </section>

        {/* Section 12 */}
        <section className="mb-10">
          <h2 className="text-2xl font-semibold mb-4">12. Contact Information</h2>
          <div className="space-y-4 text-gray-300">
            <p>For questions regarding this Agreement, contact us at:</p>
            <div className="p-4 bg-white/5 rounded-lg border border-white/10">
              <p className="text-white font-semibold">Metro City Groove (Pty) Ltd</p>
              <p className="text-gray-400">Email: partnerships@metrocitygroove.com</p>
              <p className="text-gray-400">Phone: +27 11 000 0000</p>
              <p className="text-gray-400">Address: Johannesburg, South Africa</p>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="mt-16 p-6 bg-white/5 rounded-xl border border-white/10">
          <h2 className="text-xl font-semibold mb-4">Related Documents</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link to="/terms" className="text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Terms of Service
            </Link>
            <Link to="/privacy" className="text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Privacy Policy
            </Link>
            <Link to="/business-verification-policy" className="text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Business Verification Policy
            </Link>
            <Link to="/gdpr" className="text-purple-400 hover:text-purple-300 transition-colors flex items-center gap-2">
              <FileText className="w-4 h-4" />
              GDPR Compliance
            </Link>
          </div>
        </section>
      </main>
    </div>;
};
export default AgencyPartnershipAgreement;